"use client";

import { Heart } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Safety() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.2
  });
  return <section data-section-id="1972" id="our-commitment-to-safety" className="bg-background text-foreground relative overflow-hidden py-24 px-6 sm:py-32">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 h-[28rem] w-[28rem] -translate-x-1/2 -translate-y-1/2 rounded-lg blur-3xl" style={{
      background: "radial-gradient(circle, hsla(84, 18%, 59%, 0.15) 0%, hsla(84, 18%, 59%, 0) 70%)",
      animation: "solemnFlicker 6s ease-in-out infinite"
    }} />

      <div ref={ref} className="relative mx-auto flex max-w-2xl flex-col items-center text-center transition-all duration-1000 ease-out" style={{
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? "translateY(0)" : "translateY(24px)"
    }}>
        {}
        <div className="mb-8 flex h-16 w-16 items-center justify-center rounded-lg" style={{
        color: "var(--primary)",
        filter: "drop-shadow(0 0 16px hsla(84, 18%, 59%, 0.3))",
        animation: "solemnFlicker 6s ease-in-out infinite"
      }}>
          <Heart className="h-9 w-9" strokeWidth={1.25} />
        </div>

        {}
        <span className="font-mono mb-5 text-xs uppercase tracking-widest text-primary" style={{
        textShadow: "0 0 12px hsla(84, 18%, 59%, 0.2)"
      }}>
          § 01 OUR COMMITMENT TO SAFETY
        </span>

        {}
        <h2 className="font-serif text-3xl font-medium leading-tight text-foreground sm:text-4xl md:text-5xl">
          A foundation of trust and neutrality
        </h2>

        <p className="font-default mt-7 max-w-xl text-base leading-relaxed text-foreground/90 sm:text-lg">
          Our mission is to provide a child-centered, neutral approach to every interaction. We view supervision not merely as an observation, but as a professional service dedicated to the well-being of the family unit.
        </p>

        <p className="font-default mt-4 max-w-xl text-base leading-relaxed text-foreground/80 sm:text-lg">
          Tiny to Teen Supervised Parenting & Education Services maintains a strict neutrality policy. We remain non-judgmental in all family matters, ensuring that our presence serves as a stabilizing force rather than an investigative one.
        </p>

        {}
        <p className="font-default mt-6 max-w-xl text-base leading-relaxed text-foreground/80 sm:text-lg">
          We prioritize the physical and emotional security of every child through trauma-informed practice and evidence-based principles. Our safety measures are grounded in child welfare standards, ensuring that all supervised parenting time and exchanges are conducted in a structured, neutral environment.
        </p>

        <p className="font-default mt-8 text-base italic leading-relaxed text-foreground/70 sm:text-lg">
          Professional boundaries and an explicit commitment to child safety are the cornerstones of our compassionate care.
        </p>

        <div className="mt-16 w-full space-y-12 text-left">
          <div className="border-t border-primary/20 pt-8">
            <h3 className="font-serif text-2xl font-medium text-foreground mb-4">Neutrality Policy</h3>
            <p className="font-default text-base leading-relaxed text-foreground/80">
              Our agency operates with absolute impartiality. We do not take sides in legal disputes or family conflicts. Our role is to provide a safe, objective environment where the child's needs remain the primary focus, independent of the dynamics between adult parties.
            </p>
          </div>

          <div className="border-t border-primary/20 pt-8">
            <h3 className="font-serif text-2xl font-medium text-foreground mb-4">Safety & Intervention Policy</h3>
            <p className="font-default text-base leading-relaxed text-foreground/80">
              Safety is non-negotiable. Our staff is trained to intervene immediately if a child's physical or emotional well-being is at risk. We follow strict protocols for monitoring interactions, managing transitions, and documenting observations to ensure a secure environment for all participants.
            </p>
          </div>

          <div className="border-t border-primary/20 pt-8">
            <h3 className="font-serif text-2xl font-medium text-foreground mb-4">Client/Agency Responsibilities</h3>
            <p className="font-default text-base leading-relaxed text-foreground/80">
              Successful supervised parenting requires cooperation from all parties. Clients are responsible for adhering to scheduled times, following agency guidelines, and maintaining respectful conduct. The agency is responsible for providing qualified supervision, maintaining accurate records, and ensuring a neutral setting.
            </p>
          </div>

          <div className="border-t border-primary/20 pt-8">
            <h3 className="font-serif text-2xl font-medium text-foreground mb-4">Insurance and Payment Policy</h3>
            <p className="font-default text-base leading-relaxed text-foreground/80">
              Tiny to Teen Supervised Parenting & Education Services does not accept insurance for supervised parenting time or monitored exchange services. All services require prepayment to reserve your appointment slot. Current rates: $95 per individual intake (non-refundable), $80/hour standard supervision (2-hour minimum), $160/hour holiday supervision, $45 per monitored exchange, and $2.50 per mile for transport services. Payment terms and scheduling policies are outlined in your service agreement.
            </p>
          </div>

          <div className="border-t border-primary/20 pt-8">
            <h3 className="font-serif text-2xl font-medium text-foreground mb-4">Therapeutic Supervised Visitation Policy</h3>
            <p className="font-default text-base leading-relaxed text-foreground/80">
              Therapeutic Supervised Visitation (TSV) is a specialized service designed to repair and strengthen the parent-child bond in a safe, clinical environment. Unlike standard supervision, TSV involves active intervention and guidance from a qualified professional to address specific family dynamics or trauma. Please contact us for specific TSV rates and clinical requirements.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes solemnFlicker {
          0%, 100% { opacity: 1; }
          45% { opacity: 0.82; }
          55% { opacity: 0.92; }
          70% { opacity: 0.78; }
        }
      `}</style>
    </section>;
}